import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useOnboardingTaskProgress, type NavContentProps } from '@frontend/product-onboarding';
import { useIsSmallMode } from '@frontend/shared';
import { useShellTheme } from '@frontend/shell-theme';
import { theme } from '@frontend/theme';
import { Text, NotificationBadge } from '@frontend/design-system';
import { useBaseOnboardingProgress } from '../hooks/use-base-onboarding-progress';

export function GetStartedContent({ hasIncompleteSteps, isOpen, completedHotspots, flow }: NavContentProps) {
  const { t } = useTranslation('call-intelligence');
  const { colorObject } = useShellTheme('colorObject');
  const { isSmallMode } = useIsSmallMode();

  const { completedGroups } = useBaseOnboardingProgress();
  const { pendingCount } = useOnboardingTaskProgress({ flow, completedHotspots, completedGroups });

  if (isSmallMode) {
    return (
      <div css={styles.smallModeWrapper}>
        {hasIncompleteSteps && (
          <NotificationBadge truncateCount css={styles.notificationBadge}>
            {pendingCount}
          </NotificationBadge>
        )}
        <Icon name='action' css={{ minWidth: 24 }} />
      </div>
    );
  }

  return (
    <div
      css={[
        styles.navItem,
        styles.iconColor(colorObject.iconColor),
        hasIncompleteSteps && styles.statusDot(isOpen),
        isOpen ? styles.activeBackground(colorObject.hover) : styles.hoverState(colorObject),
      ]}
    >
      <Icon
        name='action'
        size={20}
        css={{
          color: isOpen ? colorObject.activeIconColor : 'inherit',
          fontSize: 'inherit',
          transition: 'color 0.3s ease',
        }}
      />
      <Text
        as='span'
        size='small'
        weight={isOpen ? 'bold' : 'regular'}
        css={{ color: isOpen ? colorObject.activeIconColor : 'inherit' }}
      >
        {t('Get Started')}
      </Text>
    </div>
  );
}

const styles = {
  navItem: css`
    text-decoration: none;
    align-items: center;
    display: flex;
    flex-direction: column;
    font-size: var(--nav-item-font-size);
    height: 3.5em;
    justify-content: center;
    position: relative;
    transition: width 0.2s ease-in-out, background 0.2s ease-in-out;
    width: var(--nav-icon-width);
    background: var(--nav-background-color);
  `,

  iconColor: (color: string) => css`
    color: ${color};
  `,

  activeBackground: (hoverColor: string) => css`
    background-color: ${hoverColor};
  `,

  hoverState: (colorObject: { hover: string; activeIconColor: string }) => css`
    :hover {
      background: ${colorObject.hover};
      color: ${colorObject.activeIconColor};
    }
  `,

  statusDot: (isOpen: boolean) => css`
    ::after {
      background: ${theme.colors.critical50};
      border-radius: 16px;
      border: 1px solid ${isOpen ? 'var(--colors-white)' : 'var(--nav-background-color)'};
      content: '';
      height: 10px;
      left: 45px;
      opacity: 1;
      position: absolute;
      top: 6px;
      transition: border-color, opacity ease-in-out 250ms;
      width: 10px;
      z-index: 10;
    }
  `,
  notificationBadge: css`
    position: absolute;
    left: -20px;
    top: -16px;
    padding: ${theme.spacing(0.5)};
    height: ${theme.spacing(2)};
    width: ${theme.spacing(2)};
  `,

  smallModeWrapper: css`
    display: flex;
    align-items: center;
    position: relative;
  `,
};
